import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../api";
import { useNavigate } from "react-router-dom";
import { useNotification } from "./NotificationContext";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [fetchError, setFetchError] = useState(false);
    const navigate = useNavigate();
    const { showToast } = useNotification();

    const logout = useCallback((message, type = "info") => {
        console.log("Deconnexion", message || "volontaire");
        localStorage.removeItem("token");
        setUser(null);
        setIsLoading(false);
        if (typeof message === "string") {
            showToast(message, type);
        }
        navigate("/login");
    }, [navigate, showToast]);

    useEffect(() => {
        const token = localStorage.getItem("token");
    
        if (!token) {
            setIsLoading(false);
            return;
        }
    
        const fetchUser = async () => {
            try {
                const res = await api.post("/utilisateurs/getUser");
        
                const preferences = res.data.preferences || {};
                setUser({
                    id: res.data.id,
                    Nom: res.data.Nom,  
                    Email: res.data.Email,
                    jobTitle: res.data.jobTitle,
                    roles: res.data.roles || [],
                    roleNames: res.data.roleNames || [],
                    workspaceId: res.data.workspaceId,
                    membershipId: res.data.membershipId,
                    createdAt: res.data.createdAt,
                    isTwoFactorEnabled: res.data.isTwoFactorEnabled,
                    profileImage: res.data.profileImage,
                    permissions: res.data.permissions || [],
                    preferences
                });

                document.documentElement.classList.toggle("dark", preferences.darkMode === true);
                if (preferences.density) {
                    document.documentElement.setAttribute("data-density", preferences.density);
                }
                if (preferences.language) {
                    document.documentElement.lang = preferences.language;
                }
        
                setFetchError(false);
            } catch (err) {
                if (err.message === "Network Error") {
                    setFetchError("Erreur reseau : impossible de contacter le serveur.");
                } else {
                    setFetchError(true);
                }
            } finally {
                setIsLoading(false);
            }
        };                

        fetchUser();
    }, []);    

    const login = (utilisateur) => {
        console.log("Tentative de login dans AuthProvider, reçu:", utilisateur);
        if (!utilisateur || !utilisateur.Email) {
            console.error("Login avorté car utilisateur ou utilisateur.Email est manquant !", utilisateur);
            return;
        }

        setUser({
            id: utilisateur.id,
            Nom: utilisateur.Nom,
            Email: utilisateur.Email,
            jobTitle: utilisateur.jobTitle,
            roles: utilisateur.roles || [],
            roleNames: utilisateur.roleNames || [],
            workspaceId: utilisateur.workspaceId,
            membershipId: utilisateur.membershipId,
            createdAt: utilisateur.createdAt,
            isTwoFactorEnabled: utilisateur.isTwoFactorEnabled,
            profileImage: utilisateur.profileImage,
            permissions: utilisateur.permissions || []
        });

        showToast(`Bienvenue, ${utilisateur.Nom} !`, "success");
    };    

    return (
        <AuthContext.Provider value={{ user, login, logout, isLoading, fetchError }}>
            {children}
        </AuthContext.Provider>
    );
};
